import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AnnotationEditor } from '@/components/diffs/AnnotationEditor';
import { supabase } from '@/integrations/supabase/client';
import { ComparisonData, ComparisonMarker } from '@/types/sharedLinks';
import { ExampleTableRow } from './types';

interface Annotation {
  num: number;
  label: string;
  parent_label?: string;
  image?: string;
  before: { x: number; y: number } | null;
  after: { x: number; y: number } | null;
  marker_before?: { x: number; y: number };
  marker_after?: { x: number; y: number };
  viewport_before?: { zoom: number; panX: number; panY: number };
  viewport_after?: { zoom: number; panX: number; panY: number };
  hideMarker1?: boolean;
  hideMarker2?: boolean;
}

interface AnnotationCoordinates {
  cloudinary_before?: string;
  cloudinary_after?: string;
  annotations: Annotation[];
}

interface EditTableExampleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: ExampleTableRow;
  onSave: (updatedRow: ExampleTableRow) => void;
}

export const EditTableExampleModal: React.FC<EditTableExampleModalProps> = ({
  open,
  onOpenChange,
  row,
  onSave
}) => {
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [annotationCoordinates, setAnnotationCoordinates] = useState<AnnotationCoordinates | null>(null);

  // Fetch current comparison_data from app_shared_links
  useEffect(() => {
    if (open && row.sharedLinkId) {
      fetchComparisonData();
    }
  }, [open, row.sharedLinkId]);

  const fetchComparisonData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('app_shared_links')
        .select('comparison_data')
        .eq('id', row.sharedLinkId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      if (data?.comparison_data) {
        console.log('Loaded comparison_data from database:', data.comparison_data);
        console.log('Row annotation data:', row.annotation);
        setComparisonData(data.comparison_data);

        // Convert ALL markers from ComparisonData to AnnotationCoordinates format
        const markers = data.comparison_data.markers || [];
        console.log('Markers from comparison_data:', markers);
        const annotations: Annotation[] = markers.map((marker, index) => ({
          num: index + 1,
          label: marker.label || row.eventType,
          parent_label: row.companyName,

          // Original crop centers (stable, for centering)
          before: { x: marker.x1, y: marker.y1 },
          after: { x: marker.x2, y: marker.y2 },

          // Explicit marker positions (moveable)
          marker_before: { x: marker.x1, y: marker.y1 },
          marker_after: { x: marker.x2, y: marker.y2 },

          // Viewport state (load from database or use defaults)
          viewport_before: data.comparison_data.viewport1 || { zoom: 1.0, panX: 0, panY: 0 },
          viewport_after: data.comparison_data.viewport2 || { zoom: 1.0, panX: 0, panY: 0 },

          // Hide flags
          hideMarker1: marker.hideMarker1 || false,
          hideMarker2: marker.hideMarker2 || false,
        }));

        // If no markers exist, try to get them from row.annotation
        if (annotations.length === 0 && row.annotation) {
          const beforeMarker = row.annotation.before_marker || row.annotation.before;
          const afterMarker = row.annotation.after_marker || row.annotation.after;

          if (beforeMarker || afterMarker) {
            annotations.push({
              num: 1,
              label: row.eventType,
              parent_label: row.companyName,
              before: beforeMarker ? { x: beforeMarker.x, y: beforeMarker.y } : (afterMarker ? { x: afterMarker.x, y: afterMarker.y } : { x: 400, y: 300 }),
              after: afterMarker ? { x: afterMarker.x, y: afterMarker.y } : (beforeMarker ? { x: beforeMarker.x, y: beforeMarker.y } : { x: 400, y: 300 }),
              marker_before: beforeMarker ? { x: beforeMarker.x, y: beforeMarker.y } : undefined,
              marker_after: afterMarker ? { x: afterMarker.x, y: afterMarker.y } : undefined,
              viewport_before: data.comparison_data.viewport1 || { zoom: 1.0, panX: 0, panY: 0 },
              viewport_after: data.comparison_data.viewport2 || { zoom: 1.0, panX: 0, panY: 0 },
            });
          }
        }

        // If still no markers, create one default marker at center
        if (annotations.length === 0) {
          annotations.push({
            num: 1,
            label: row.eventType,
            parent_label: row.companyName,
            before: { x: 400, y: 300 },
            after: { x: 400, y: 300 },
            marker_before: { x: 400, y: 300 },
            marker_after: { x: 400, y: 300 },
            viewport_before: data.comparison_data.viewport1 || { zoom: 1.0, panX: 0, panY: 0 },
            viewport_after: data.comparison_data.viewport2 || { zoom: 1.0, panX: 0, panY: 0 },
          });
        }

        const coords: AnnotationCoordinates = {
          annotations
        };

        console.log('Final annotations to display:', annotations);
        setAnnotationCoordinates(coords);
      }
    } catch (err) {
      console.error('Error fetching comparison data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!comparisonData || !annotationCoordinates || annotationCoordinates.annotations.length === 0) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Convert ALL annotations back to ComparisonMarker format
      const updatedMarkers: ComparisonMarker[] = annotationCoordinates.annotations.map(annotation => ({
        x1: annotation.marker_before?.x || annotation.before?.x || 0,
        y1: annotation.marker_before?.y || annotation.before?.y || 0,
        x2: annotation.marker_after?.x || annotation.after?.x || 0,
        y2: annotation.marker_after?.y || annotation.after?.y || 0,
        label: annotation.label,
        hideMarker1: annotation.hideMarker1 || false,
        hideMarker2: annotation.hideMarker2 || false,
      }));

      // Extract viewport from first annotation (all annotations share viewport in linked mode)
      const firstAnnotation = annotationCoordinates.annotations[0];

      const updatedComparisonData: ComparisonData = {
        ...comparisonData,
        markers: updatedMarkers,
        viewport1: firstAnnotation.viewport_before || { zoom: 1.0, panX: 0, panY: 0 },
        viewport2: firstAnnotation.viewport_after || { zoom: 1.0, panX: 0, panY: 0 },
      };

      console.log('Updating app_shared_links with:', {
        id: row.sharedLinkId,
        markerCount: updatedMarkers.length,
        updatedMarkers,
        updatedComparisonData
      });

      // Update app_shared_links.comparison_data
      const { data: updateData, error: updateError } = await supabase
        .from('app_shared_links')
        .update({
          comparison_data: updatedComparisonData,
          updated_at: new Date().toISOString()
        })
        .eq('id', row.sharedLinkId)
        .select();

      console.log('Update result:', { updateData, updateError });

      if (updateError) {
        throw updateError;
      }

      // Verify the update by reading it back
      const { data: verifyData, error: verifyError } = await supabase
        .from('app_shared_links')
        .select('comparison_data')
        .eq('id', row.sharedLinkId)
        .single();

      console.log('Verification read:', { verifyData, verifyError });

      if (verifyError) {
        console.error('Verification failed:', verifyError);
      }

      // Update row annotation (for regeneration purposes) - use first marker
      const firstMarker = updatedMarkers[0];
      const updatedRow: ExampleTableRow = {
        ...row,
        annotation: row.annotation ? {
          ...row.annotation,
          before_marker: firstMarker ? { x: firstMarker.x1, y: firstMarker.y1 } : undefined,
          after_marker: firstMarker ? { x: firstMarker.x2, y: firstMarker.y2 } : undefined,
        } : firstMarker ? {
          before_marker: { x: firstMarker.x1, y: firstMarker.y1 },
          after_marker: { x: firstMarker.x2, y: firstMarker.y2 },
        } : undefined
      };

      onSave(updatedRow);
      onOpenChange(false);
    } catch (err) {
      console.error('Error saving comparison data:', err);
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (coords: AnnotationCoordinates) => {
    setAnnotationCoordinates(coords);
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit Markers</DialogTitle>
          <DialogDescription>
            {row.companyName} - {row.description}
            <br />
            <span className="text-xs text-gray-500">
              Drag markers to reposition • Click "+" to add markers • Check boxes to hide markers on individual sides • Click trash icon to remove markers
            </span>
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 text-red-600 px-4 py-2 rounded-md text-sm">
            {error}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-gray-500">Loading...</div>
          </div>
        )}

        {!loading && annotationCoordinates && comparisonData && (
          <div className="flex-1 overflow-auto">
            <AnnotationEditor
              annotationCoordinates={annotationCoordinates}
              primaryOriginalUrl={comparisonData.url1}
              secondaryOriginalUrl={comparisonData.url2}
              onChange={handleChange}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading || saving || !annotationCoordinates}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
